/**
 * InstrumentBehaviorRegistry.js
 *
 * A scalable registry for instrument-specific logic (Doubling, Tuning, Ornamentation).
 * Replaces hardcoded switch statements in InstrumentBehaviors.js.
 */

import { ABSTRACT_ROLES } from '../RoleAllocator.js';
import { getTimpaniPitch } from './PercussionLogic.js';

// --- Behavior Functions ---

/**
 * French Horn Logic: Melody Doubling.
 * If Horns play melody, they often double a 5th below for thickness.
 *
 * @param {Array} events - The generated events.
 * @param {Object} assignment - The allocation assignment.
 * @param {string} role - The abstract role.
 * @param {Array} sectionChords - The chord progression.
 * @returns {Array} Modified events.
 */
const hornBehavior = (events, assignment, role, sectionChords) => {
    if (role !== ABSTRACT_ROLES.MELODY) return events;

    return events.flatMap(ev => {
        const fifth = ev.d - 7;
        // Check range limits (assignment.instrument.range)
        const minPitch = assignment.instrument.range?.min || 0;

        const extras = (fifth >= minPitch)
            ? [{ ...ev, d: fifth, role: 'harmony', vel: (ev.vel || 0.7) * 0.8 }]
            : [];
        return [ev, ...extras];
    });
};

/**
 * Timpani Logic: Tuning.
 * Timpani must be tuned to the chord root or 5th.
 *
 * @param {Array} events - The generated events.
 * @param {Object} assignment - The allocation assignment.
 * @param {string} role - The abstract role.
 * @param {Array} sectionChords - The chord progression.
 * @returns {Array} Modified events.
 */
const timpaniBehavior = (events, assignment, role, sectionChords) => {
    return events.map(ev => {
        // Find current chord for this event
        const chord = sectionChords.find(c =>
            c.barIndex * 4 <= ev.s && (c.barIndex * 4 + c.duration) > ev.s
        ) || sectionChords[0];

        if (chord) {
            const tuned = getTimpaniPitch(chord.rootPitchClass);
            return { ...ev, d: tuned, notes: [tuned] };
        }
        return ev;
    });
};

// --- Registry Map ---

const BEHAVIOR_MAP = {
    'horn': hornBehavior,
    'french_horn': hornBehavior, // Alias
    'timpani': timpaniBehavior
};

/**
 * Retrieves the behavior function for a specific instrument ID.
 * @param {string} instrumentId
 * @returns {Function|null} The behavior function or null.
 */
export const getInstrumentBehavior = (instrumentId) => {
    return BEHAVIOR_MAP[instrumentId] || null;
};
